/**
 * @file Quiz-bank JSON serialization ↔ rehydration — the contract that
 *       lets a `RegExp` `accept` entry survive authoring → build →
 *       runtime (umbrella #1203 Phase 3, Part D).
 * @module @labcraft/course-kit
 *
 * A `command` / `fill-blank` question's `accept` list can hold `RegExp`
 * entries (a family of acceptable phrasings, matched by
 * `entry.test(normalized)` in the engine). `RegExp` does **not**
 * JSON-serialize — `JSON.stringify(/foo/i)` emits `{}` — so a naïve
 * `spec.json` would drop every regex the moment the builder writes it.
 *
 * The fix is a tagged wire form. On the way **out** (the builder, before
 * `JSON.stringify`), {@link serializeChapterSpecForJson} rewrites every
 * `RegExp` accept entry to a {@link SerializedRegExp} tag carrying the
 * regex's `source` and `flags`. On the way **in** (the runtime, after
 * `JSON.parse`), {@link rehydrateChapterSpec} reconstructs a real
 * `RegExp` from each tag with `new RegExp(source, flags)`. String accept
 * entries pass through untouched — they already round-trip through JSON.
 *
 * The tag shape (`{ __lcRegex: { source, flags } }`) is chosen so:
 *
 *   - it can't collide with any legitimate authored value (`__lcRegex`
 *     is a reserved sentinel; a string accept entry is a bare string,
 *     never an object);
 *   - it survives an arbitrary number of JSON round-trips (disk →
 *     manifest, then manifest → HTTP wire → SPA), so a consumer at any
 *     hop rehydrates the same way — the SPA rehydrates the spec it
 *     receives over the wire exactly as the lab daemon would rehydrate
 *     the spec it reads off disk;
 *   - rehydration is idempotent on an already-real `RegExp` (a spec that
 *     never crossed JSON — e.g. an in-process test — rehydrates to
 *     itself), so callers need not track whether a spec has been through
 *     the wire.
 *
 * This is `design.md` § "Follow the already-coherent side" applied to a
 * serialization gap: rather than teach the engine to consume two answer
 * shapes (real regex vs. tagged), one narrow serde seam keeps the engine
 * on the single `RegExp` shape it already grades against.
 */

import type {
  ChapterSpec,
  PartAssessment,
  QuizAnswer,
  QuizQuestion,
} from './types.ts'

/**
 * The runtime `accept` element list of the free-text answer variant —
 * the `command` / `fill-blank` member of {@link QuizAnswer}. Extracted so
 * the serde cast targets the free-text variant's `accept` type rather
 * than indexing the whole `QuizAnswer` union (which has no `accept` on
 * its `mc` member).
 */
type FreeTextAccept = Extract<
  QuizAnswer,
  { kind: 'command' | 'fill-blank' }
>['accept']

/** The reserved sentinel key marking a serialized `RegExp` tag. */
const REGEX_TAG = '__lcRegex'

/**
 * The JSON-safe wire form of a `RegExp` accept entry. A tagged object
 * under the reserved {@link REGEX_TAG} key so it can't be mistaken for a
 * plain-string accept entry (which is a bare `string`, never an object).
 *
 * @property __lcRegex - the regex's reconstruction inputs
 * @property __lcRegex.source - `RegExp.prototype.source` (the pattern)
 * @property __lcRegex.flags - `RegExp.prototype.flags` (e.g. `"i"`,
 *   `""`)
 */
export interface SerializedRegExp {
  [REGEX_TAG]: { source: string; flags: string }
}

/**
 * A `RegExp` accept entry as it appears on the wire: either the tagged
 * {@link SerializedRegExp} form (post-serialize, pre-rehydrate) or the
 * plain `string` that JSON already round-trips. This is the element type
 * of a serialized `accept` array; {@link rehydrateChapterSpec} maps it
 * back to `string | RegExp`.
 */
export type SerializedAcceptEntry = string | SerializedRegExp

/**
 * Course-kit's serde surface for the quiz bank. Static methods per
 * `typescript.md` § "Functions belong on classes" — a namespace of pure
 * transforms over a {@link ChapterSpec}, tied to no instance. The
 * builder calls {@link QuizSerde.serializeChapterSpecForJson} before
 * `JSON.stringify`; every runtime consumer calls
 * {@link QuizSerde.rehydrateChapterSpec} after `JSON.parse`.
 */
export class QuizSerde {
  /**
   * Rewrite a chapter spec into a JSON-safe form: every `RegExp` accept
   * entry in the quiz bank becomes a {@link SerializedRegExp} tag; all
   * other fields are copied structurally. Returns a fresh spec — the
   * input is never mutated, so a course's in-memory `Course` value (used
   * by the runtime manifest and by tests) keeps its real regexes.
   *
   * A spec with no `quiz`, or a quiz whose questions carry no `RegExp`
   * accept entries, is returned structurally unchanged (a shallow copy).
   *
   * @param spec - the chapter spec to serialize
   * @returns a spec whose quiz `accept` regexes are tagged
   */
  static serializeChapterSpecForJson(spec: ChapterSpec): ChapterSpec {
    if (!spec.quiz) return { ...spec }
    return {
      ...spec,
      quiz: {
        ...spec.quiz,
        questions: spec.quiz.questions.map(q => QuizSerde.serializeQuestion(q)),
      },
    }
  }

  /**
   * Reconstruct a chapter spec from its JSON-parsed form: every tagged
   * {@link SerializedRegExp} accept entry becomes a real `RegExp` via
   * `new RegExp(source, flags)`; string entries and already-real
   * `RegExp`s pass through. Idempotent — safe to call on a spec that
   * never crossed JSON. Returns a fresh spec; the input is not mutated.
   *
   * Consumed at every JSON→object boundary the spec crosses: the lab
   * daemon after reading `manifest.json` off disk, and the SPA after
   * receiving the spec over the wire — both call this so the quiz engine
   * always grades against real `RegExp`s.
   *
   * @param spec - the JSON-parsed chapter spec
   * @returns a spec whose quiz `accept` tags are real `RegExp`s
   */
  static rehydrateChapterSpec(spec: ChapterSpec): ChapterSpec {
    if (!spec.quiz) return { ...spec }
    return {
      ...spec,
      quiz: {
        ...spec.quiz,
        questions: spec.quiz.questions.map(q => QuizSerde.rehydrateQuestion(q)),
      },
    }
  }

  /**
   * Rewrite a {@link PartAssessment} into a JSON-safe form (umbrella
   * #1357 Part B): every `RegExp` accept entry across the consolidated
   * `questions` bank becomes a {@link SerializedRegExp} tag; every other
   * field (`concepts`, `startBand`, `remediation`) is copied structurally.
   * The bank-level analogue of {@link QuizSerde.serializeChapterSpecForJson}
   * — the Part's `assessment.ts` is a flat bank with no enclosing
   * `ChapterSpec`, so it needs its own bank-level entry point. Returns a
   * fresh assessment; the input is never mutated (a course's in-memory
   * value keeps its real regexes). The builder calls this before
   * `JSON.stringify`.
   *
   * @param a - the Part assessment to serialize
   * @returns an assessment whose bank `accept` regexes are tagged
   */
  static serializePartAssessmentForJson(a: PartAssessment): PartAssessment {
    return {
      ...a,
      questions: a.questions.map(q => QuizSerde.serializeQuestion(q)),
    }
  }

  /**
   * Reconstruct a {@link PartAssessment} from its JSON-parsed form
   * (umbrella #1357 Part B): every tagged {@link SerializedRegExp} accept
   * entry across the bank becomes a real `RegExp`; string entries and
   * already-real `RegExp`s pass through. Idempotent — safe on an
   * assessment that never crossed JSON. Returns a fresh assessment; the
   * input is not mutated. The reader calls this wherever the Part bank
   * crosses a JSON→object boundary (the lab daemon after reading the
   * manifest off disk, the SPA after receiving it over the wire), the
   * analogue of {@link QuizSerde.rehydrateChapterSpec}.
   *
   * @param a - the JSON-parsed Part assessment
   * @returns an assessment whose bank `accept` tags are real `RegExp`s
   */
  static rehydratePartAssessment(a: PartAssessment): PartAssessment {
    return {
      ...a,
      questions: a.questions.map(q => QuizSerde.rehydrateQuestion(q)),
    }
  }

  /**
   * Serialize one question's answer if it is free-text; `mc` answers
   * carry no regex and pass through unchanged.
   *
   * @param question - the question to serialize
   * @returns a question whose free-text `accept` regexes are tagged
   */
  private static serializeQuestion(question: QuizQuestion): QuizQuestion {
    if (question.answer.kind === 'mc') return question
    const accept = question.answer.accept.map(entry =>
      entry instanceof RegExp ? QuizSerde.tag(entry) : entry,
    )
    // The tagged form is not assignable to the free-text answer's
    // `accept` (`(string | RegExp)[]`); the cast crosses the serde seam,
    // where the array holds wire tags that JSON round-trips and rehydrate
    // reverses. Isolated to this one call site so the runtime `accept`
    // type stays honest everywhere else.
    return {
      ...question,
      answer: {
        ...question.answer,
        accept: accept as unknown as FreeTextAccept,
      },
    } as QuizQuestion
  }

  /**
   * Rehydrate one question's answer if it is free-text; `mc` answers
   * pass through unchanged.
   *
   * @param question - the JSON-parsed question
   * @returns a question whose free-text `accept` tags are real `RegExp`s
   */
  private static rehydrateQuestion(question: QuizQuestion): QuizQuestion {
    if (question.answer.kind === 'mc') return question
    const accept = (
      question.answer.accept as unknown as SerializedAcceptEntry[]
    ).map(entry => (QuizSerde.isTag(entry) ? QuizSerde.untag(entry) : entry))
    return {
      ...question,
      answer: { ...question.answer, accept },
    } as QuizQuestion
  }

  /**
   * Build a {@link SerializedRegExp} tag from a real `RegExp`.
   *
   * @param regex - the regex to serialize
   * @returns its JSON-safe tag
   */
  private static tag(regex: RegExp): SerializedRegExp {
    return { [REGEX_TAG]: { source: regex.source, flags: regex.flags } }
  }

  /**
   * Reconstruct a real `RegExp` from a {@link SerializedRegExp} tag.
   *
   * @param tag - the serialized tag
   * @returns the reconstructed `RegExp`
   */
  private static untag(tag: SerializedRegExp): RegExp {
    return new RegExp(tag[REGEX_TAG].source, tag[REGEX_TAG].flags)
  }

  /**
   * Type guard: whether a serialized accept entry is a
   * {@link SerializedRegExp} tag (vs. a plain string or an already-real
   * `RegExp` that never crossed JSON).
   *
   * @param entry - the entry to test
   * @returns true when the entry is a regex tag
   */
  private static isTag(
    entry: SerializedAcceptEntry | string | RegExp,
  ): entry is SerializedRegExp {
    return (
      typeof entry === 'object' &&
      entry !== null &&
      !(entry instanceof RegExp) &&
      REGEX_TAG in entry
    )
  }
}
